import {
	getConversationQueryKey,
	getConversationSuspenseQueryOptions,
	listConversationsQueryKey,
	useDeleteConversation as useDeleteConversationBase,
} from "@packages/api-client/hooks/chatController";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ConversationSummary {
	id: string;
	title: string;
	lastMessageAt: string;
	messageCount: number;
	establishmentId: string | null;
	establishmentName: string | null;
}

export interface ConversationMessage {
	id: string;
	role: "user" | "assistant" | "system" | "tool";
	content: string;
	timestamp: string;
	agentName: string | null;
}

export interface ConversationDetail {
	id: string;
	establishmentId: string | null;
	title: string;
	messages: ConversationMessage[];
	createdAt: string;
	updatedAt: string;
}

// ─── Re-exports ──────────────────────────────────────────────────────────────

export { listConversationsQueryKey };

// ─── Query Options ────────────────────────────────────────────────────────────

export const fetchConversationQueryOptions = (
	organizationId: string,
	conversationId: string,
) => getConversationSuspenseQueryOptions(organizationId, conversationId);

// ─── Mutation Hooks ───────────────────────────────────────────────────────────

export const useDeleteConversation = (organizationId: string) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useDeleteConversationBase({
		mutation: {
			meta: {
				toastMessages: {
					loading: t("common.deleting"),
					success: t("common.deleteSuccess"),
				},
			},
			onSuccess: (_data, variables) => {
				queryClient.invalidateQueries({
					queryKey: listConversationsQueryKey(organizationId),
				});
				queryClient.removeQueries({
					queryKey: getConversationQueryKey(
						variables.organizationId,
						variables.conversationId,
					),
				});
			},
		},
	});
};
